// @flow
import React, {useEffect, useRef} from 'react'
import styled from '@emotion/styled';
import { colors } from '@atlaskit/theme';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import Card from './card';
import { grid } from '../constants';
import Title from './title';
import type { Quote } from '../types';
import type {
  DroppableProvided,
  DroppableStateSnapshot,
  DraggableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';
import CardEdit from "./CardEdit";
import plusImg from "../assets/plus.png";
import { announce } from "./../../util/liveRegion.js";

export const getBackgroundColor = (
  isDraggingOver: boolean,
  isDraggingFrom: boolean,
): string => {
  // if (isDraggingOver) {
  //   return colors.R50;
  // }
  // if (isDraggingFrom) {
  //   return colors.T50;
  // }
  return "#f7f5f4";
};

const Wrapper = styled.div`
  background-color: rgb(247, 245, 244 , 0.8);  
  display: flex;
  flex-direction: column;
  opacity: ${({ isDropDisabled }) => (isDropDisabled ? 0.5 : 'inherit')};
  padding: 15px;
  padding-top: 5px;
  border: ${grid}px;
  padding-bottom: 0;
  transition: background-color 0.2s ease, opacity 0.1s ease;
  user-select: none;
  width: auto;
  box-shadow: 0px 2px 2px rgb(0 0 0 / 10%);
  border-bottom-left-radius: 7px;
  border-bottom-right-radius: 7px;
`;

// Enable for scrollable contnet to set column hight based on user browser height:
//const scrollContainerHeight: number = window.innerHeight ? window.innerHeight - 100 : 1000;

const scrollContainerHeight: number = 250;

const DropZone = styled.div`
  /* stop the list collapsing when empty */
  min-height: ${scrollContainerHeight}px;

  /*
    not relying on the items for a margin-bottom
    as it will collapse when the list is empty
  */
  padding-bottom: ${grid}px;
`;

const ScrollContainer = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  max-height: ${scrollContainerHeight}px;
`;

const AddCardButton = styled.button`
  background: none;
  border: none;
  margin-bottom: 10px;
  outline: none;
  padding: 10px;
  width: 100%;
  font-family: 'Segoe UI Regular';
  color: #5c6263;
  border-radius: 5px;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.2s ease;
  font-size: 0.8rem;
  &:hover {
    background-color: #eee9e7;
  }
`;

const Icon = styled.img`
  height: ${props => props.height ? props.height : "11"}px;
  display: inline-block;
  padding-right: 5px;
`;

/* stylelint-disable block-no-empty */
const Container = styled.div``;
/* stylelint-enable */

type Props = {|
  listId?: string,
  listType?: string,
  cards: Quote[],
  title?: string,
  internalScroll?: boolean,
  scrollContainerStyle?: Object,
  isDropDisabled?: boolean,
  isCombineEnabled?: boolean,
  style?: Object,
  // may not be provided - and might be null
  ignoreContainerClipping?: boolean,

  useClone?: boolean,
|};

type QuoteListProps = {|
  cards: Quote[],
|};

const InnerQuoteList = React.memo(function InnerQuoteList(
  props: QuoteListProps,
) {
  const totalCards = props.cards.length;
  return props.cards.map((card: Quote, index: number) => (
    <Draggable key={card.cardId} draggableId={card.cardId} index={index} isDragDisabled={props.isFilterActive}>
      {(
        dragProvided: DraggableProvided,
        dragSnapshot: DraggableStateSnapshot,
      ) => (
        <Card
          key={card.id}
          card={card}
          mapOfAllTags={props.mapOfAllTags}
          isDragging={dragSnapshot.isDragging}
          isGroupedOver={Boolean(dragSnapshot.combineTargetFor)}
          provided={dragProvided}
          onMoveCard={props.onMoveCard}
          isFirstCard={index === 0}
          isLastCard={index === totalCards - 1}
          isFirstColumn={props.isFirstColumn}
          isLastColumn={props.isLastColumn}
          isFilterActive={props.isFilterActive}
          previousColumnTitle={props.previousColumnTitle}
          nextColumnTitle={props.nextColumnTitle}
          columnTitle={props.columnTitle}
        />
      )}
    </Draggable>
  ));
});

type InnerListProps = {|
  dropProvided: DroppableProvided,
  cards: Quote[],
  title: ?string,
|};

class InnerList extends React.PureComponent {
  constructor(props) {
    super(props);
    this._addCardButtonRef = React.createRef();
    this.state = {
      isCreatingCard: false,
      content: null,
      tags: null
    }
  }

  focusAddCardButtonAfterRender = () => {
    requestAnimationFrame(() => {
      const el = this._addCardButtonRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    });
  }

  onCardCreateSuccess = (newContent, newTags) => {
    this.setState({content: newContent, tags: newTags, isCreatingCard: false}, this.focusAddCardButtonAfterRender);
    announce('New card added');
  }

  onCardCreateCancel = () => {
    this.setState({isCreatingCard: false, content: null, newTags: null}, this.focusAddCardButtonAfterRender);
  }

  render() {
    const { cards, dropProvided, mapOfAllTags, columnId, isFilterActive, onMoveCard, isFirstColumn, isLastColumn, previousColumnTitle, nextColumnTitle, columnTitle } = this.props;
    const title = this.props.title ? <Title>{this.props.title}</Title> : null;
    const {isCreatingCard} = this.state;

    const createCardElement = <CardEdit
      onUpdateSuccess={this.onCardCreateSuccess} 
      onCancel={this.onCardCreateCancel}
      columnId={columnId}
      {...this.props}
    />;
    
    return (
      <Container>
        {title}
        {!isCreatingCard &&
          <AddCardButton ref={this._addCardButtonRef} type="button" aria-label={columnTitle ? `Add a card to ${columnTitle}` : 'Add a new card'} onClick={() => this.setState({isCreatingCard: true})}>
            <Icon src={plusImg} alt="" aria-hidden="true" />Add new card
          </AddCardButton>}
        {isCreatingCard ? createCardElement : null}        
        <DropZone ref={dropProvided.innerRef} role="list" aria-label={columnTitle ? `Cards in ${columnTitle}` : 'Cards'}>
          <InnerQuoteList
            cards={cards}
            mapOfAllTags={mapOfAllTags}
            isFilterActive={isFilterActive}
            onMoveCard={onMoveCard}
            isFirstColumn={isFirstColumn}
            isLastColumn={isLastColumn}
            previousColumnTitle={previousColumnTitle}
            nextColumnTitle={nextColumnTitle}
          />
          {dropProvided.placeholder}
        </DropZone>
      </Container>
    );
  }
}

export default class CardsList extends React.PureComponent {

  constructor() {
    super();
  }

  render() {
    const {
      ignoreContainerClipping,
      internalScroll,
      scrollContainerStyle,
      isDropDisabled,
      isCombineEnabled,
      listId = 'LIST',
      listType,
      style,
      cards,
      title,
      useClone,
      mapOfAllTags,
      isFilterActive,
      onMoveCard,
      isFirstColumn,
      isLastColumn,
      previousColumnTitle,
      nextColumnTitle,
      columnTitle,
    } = this.props;
  
    return (
      <Droppable
        droppableId={listId}
        type={listType}
        isDropDisabled={isFilterActive}
        ignoreContainerClipping={ignoreContainerClipping}
        isDropDisabled={isDropDisabled}
        isCombineEnabled={isCombineEnabled}
        renderClone={
          useClone
            ? (provided, snapshot, descriptor) => (
                <Card
                  card={cards[descriptor.source.index]}
                  provided={provided}
                  isDragging={snapshot.isDragging}
                  mapOfAllTags={mapOfAllTags}
                  isClone
                />
              )
            : null
        }
      >
        {(
          dropProvided: DroppableProvided,
          dropSnapshot: DroppableStateSnapshot,
        ) => (
          <Wrapper
            style={style}
            isDraggingOver={dropSnapshot.isDraggingOver}
            isDropDisabled={isDropDisabled}
            isDraggingFrom={Boolean(dropSnapshot.draggingFromThisWith)}
            {...dropProvided.droppableProps}
          >
            {internalScroll ? (
              <ScrollContainer style={scrollContainerStyle}>
                <InnerList
                  cards={cards}
                  title={title}
                  columnId={listId}
                  dropProvided={dropProvided}
                  mapOfAllTags={mapOfAllTags}
                  isFilterActive={isFilterActive}
                  onMoveCard={onMoveCard}
                  isFirstColumn={isFirstColumn}
                  isLastColumn={isLastColumn}
                  previousColumnTitle={previousColumnTitle}
                  nextColumnTitle={nextColumnTitle}
                  columnTitle={columnTitle}
                />
              </ScrollContainer>
            ) : (
              <InnerList
                cards={cards}
                title={title}
                columnId={listId}
                dropProvided={dropProvided}
                mapOfAllTags={mapOfAllTags}
                isFilterActive={isFilterActive}
                onMoveCard={onMoveCard}
                isFirstColumn={isFirstColumn}
                isLastColumn={isLastColumn}
                previousColumnTitle={previousColumnTitle}
                nextColumnTitle={nextColumnTitle}
                columnTitle={columnTitle}
              />
            )}
          </Wrapper>
        )}
      </Droppable>
    );
  }
  
}
